<template>
    <div class="col-sm-12 nopads">
        <div class="col-sm-12 list-content result-table">
            <div
                class="col-12"
            >
                <vue3-datatable
                    ref="datatable"
                    :rows="tableItems"
                    :columns="fields"
                    :pagination="false"
                    :sticky-header="true"
                    :height="tableItems.length > 10 ? '40em' : '100%'"
                    :sortable="true"
                    :loading="loading"
                    skin="bh-table-compact"
                    :row-class="getRowClass"
                    cell-class="routa-table-cell"
                    @row-click="rowClicked"
                >
                    <template #created_time="data">
                        {{ data.value.created }}
                    </template>
                    <template #closed_time="data">
                        {{ data.value.closed }}
                    </template>
                    <template #seek="data">
                        <span
                            class="nopads"
                            role="button"
                            :title="$t('video_recordings.jump_to_observation')"
                            @click.stop="seekObservation(data.value.id)"
                        >
                            <font-awesome-icon
                                icon="crosshairs"
                                class="row-icon"
                            />
                        </span>
                    </template>
                    <template #photo="data">
                        <span
                            class="nopads"
                            @click.stop="showPhotoViewer(data.value.id)"
                        >
                            <font-awesome-icon
                                v-if="data.value.photos && data.value.photos.length > 0"
                                icon="camera"
                                class="row-icon"
                            />
                        </span>
                    </template>
                    <template #csv="data">
                        <span
                            class="nopads"
                            @click.stop="downloadGeometry(data.value.id)"
                        >
                            <font-awesome-icon
                                :id="'coord_' + data.value.id"
                                icon="file-csv"
                                class="row-icon"
                                :title="$t('observations.download_coordinates')"
                            />
                        </span>
                    </template>
                </vue3-datatable>
            </div>
        </div>

        <Transition name="fade">
            <observation-photo-viewer
                v-if="photoObservation"
                :observation="photoObservation"
                @close="photoObservation = null"
            />
        </Transition>
    </div>
</template>

<script>
/**
 * VideoObservationsTable
 * ======================
 * Presentational table of observations for the selected work trip, reusing
 * the same vue3-datatable columns/cells as the main Observations screen so
 * the UI stays consistent. Fed the raw observation DTOs via the
 * `observations` prop; emits `row-click` with the raw observation when a row
 * is clicked (used by the parent to seek the video to that observation).
 */
import Vue3Datatable from '@bhplugin/vue3-datatable'
import { restApi } from '../mixins/RestApiMixin'
import { timeUtils } from '../mixins/TimeUtils'
import { observationHelper } from '@/components/mixins/ObservationMixin'
import { coordinateConverter } from '../mixins/CoordinateConversionMixin'
import ObservationPhotoViewer from '@/components/observation/ObservationPhotoViewer'

export default {
    name: 'VideoObservationsTable',
    components: {
        Vue3Datatable,
        ObservationPhotoViewer
    },
    mixins: [restApi, timeUtils, observationHelper, coordinateConverter],
    props: {
        observations: {
            type: Array,
            default: () => []
        },
        // Observation id to highlight, driven from outside (e.g. a map marker
        // tap in the parent). Mirrors the local selectedRowId highlight.
        selectedObservationId: {
            type: [Number, String],
            default: null
        }
    },
    emits: ['row-click', 'seek-observation'],
    data() {
        return {
            loading: false,
            photoObservation: null,
            selectedRowId: null,
            tableItems: [],
            fields: [
                { field: 'id', title: this.$t('observations.id'), sort: true, headerClass: 'routa-table-header', type: 'number' },
                { field: 'created_time', title: this.$t('observations.created_time'), sort: true, headerClass: 'routa-table-header' },
                { field: 'type_name', title: this.$t('observations.type'), sort: true, headerClass: 'routa-table-header' },
                { field: 'length', title: this.$t('observations.length'), sort: true, headerClass: 'routa-table-header' },
                { field: 'road_address', title: this.$t('observations.road_location'), sort: true, headerClass: 'routa-table-header' },
                { field: 'reporter', title: this.$t('observations.reporter'), sort: true, headerClass: 'routa-table-header' },
                { field: 'info', title: this.$t('work_time.note'), sort: true, minWidth: '20em', headerClass: 'routa-table-header' },
                { field: 'status', title: this.$t('observations.status'), sort: true, headerClass: 'routa-table-header' },
                { field: 'seek', title: '', sort: false, headerClass: 'routa-table-header' },
                { field: 'photo', title: '', sort: false, headerClass: 'routa-table-header' },
                { field: 'csv', title: '', sort: false, headerClass: 'routa-table-header' }
            ]
        }
    },
    watch: {
        observations: {
            handler() {
                this.buildTableItems()
            },
            immediate: true
        },
        // External selection (map marker tap): highlight the matching row and
        // scroll it into view. No-op when the id is null or unknown.
        selectedObservationId(id) {
            if (id === null || id === undefined) return
            if (!this.observations.some(o => o.id === id)) return
            this.setSelectedRow(id)
            this.$nextTick(() => this.scrollToSelected())
        }
    },
    methods: {
        setSelectedRow(id) {
            this.selectedRowId = id
            this.buildTableItems()
        },

        // Build the datatable rows from the observation DTOs. Done in a
        // watcher into a data array (like Observations.vue) rather than a
        // computed, so we never mutate the reactive `observations` prop.
        buildTableItems() {
            if (!this.observations || !this.observations.length) {
                this.tableItems = []
                return
            }
            this.tableItems = this.observations
                .filter(obs => obs && obs.type)
                .map(obs => this.getTableItem(obs))
        },

        getRowClass(row) {
            return row.id === this.selectedRowId
                ? 'routa-table-row selected-row row-id-' + row.id
                : 'routa-table-row row-id-' + row.id
        },

        // Scroll the currently highlighted row into view, matching the pattern
        // used by the main Observations screen (rows carry a row-id-<id> class).
        scrollToSelected() {
            if (!this.selectedRowId) return
            const rowElement = document.querySelector('.row-id-' + this.selectedRowId)
            if (rowElement) {
                rowElement.scrollIntoView({ behavior: 'auto', block: 'center' })
            }
        },

        rowClicked(item) {
            this.setSelectedRow(item ? item.id : null)
            // Emit the raw observation so the parent can seek the video.
            const raw = this.observations.find(o => o.id === (item && item.id))
            this.$emit('row-click', raw || null)
        },

        showPhotoViewer(id) {
            this.photoObservation = this.observations.find(item => item.id === id)
        },

        // Emit the observation so the parent can jump the video + map marker
        // to its position on the timeline (third sync entry point).
        seekObservation(id) {
            const observation = this.observations.find(item => item.id === id)
            if (observation) {
                this.setSelectedRow(id)
                this.$emit('seek-observation', observation)
            }
        },

        downloadGeometry(id) {
            const observation = this.observations.find(item => item.id === id)
            if (!observation) return
            const csvData = this.getGeometryAsCsv(observation)
            if (csvData) {
                const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' })
                const url = URL.createObjectURL(blob)
                const pom = document.createElement('a')
                pom.href = url
                pom.setAttribute('download', 'Routa_geometry.csv')
                pom.click()
                URL.revokeObjectURL(url)
            }
        },

        getGeometryAsCsv(observation) {
            let result = ''
            let data = []
            if (observation.geometry.point) {
                data = [[observation.geometry.point.x, observation.geometry.point.y]]
            } else {
                data = observation.geometry.line_string.points
            }
            data.forEach(point => {
                const tm35FinCoords = this.wgs84ToTm35Fin(point[1], point[0])
                result += tm35FinCoords[0] + ',' + tm35FinCoords[1] + '\r\n'
            })
            return result
        },

        // Mirror of Observations.vue getTableItem, trimmed to fields this
        // table shows. Kept identical in shape so behavior/formatting matches.
        getTableItem(observation) {
            const closedTimeStr = (observation.closed_time !== undefined && observation.closed_time !== null)
                ? this.setTime(observation.closed_time)
                : null
            let contract = observation.contract ? observation.contract.name : undefined
            if (!contract && observation.work_order) {
                contract = observation.work_order.contract_task_type.contract
                    ? observation.work_order.contract_task_type.contract.name : '-'
            }
            return {
                id: observation.id,
                reporter: observation.reporter
                    ? observation.reporter.first_name + ' ' + observation.reporter.last_name : '-',
                type_name: observation.type.name,
                group_name: observation.type.observation_group.name,
                geometry_type: observation.type.geometry_type === 1
                    ? this.$t('observations.point') : this.$t('observations.line'),
                length: observation.length ? observation.length : '',
                created_time: new Date(observation.created_time).getTime(),
                created: this.toLocalTime(observation.created_time),
                closed_time: closedTimeStr ? new Date(closedTimeStr).getTime() : null,
                closed: closedTimeStr ? this.toLocalTime(closedTimeStr) : null,
                city: observation.city ? observation.city.name : '',
                street_name: observation.street_name ? observation.street_name : '',
                road_number: observation.road_number && observation.road_number > 0 ? observation.road_number : '',
                section_number: observation.section_number && observation.section_number > 0 ? observation.section_number : '',
                contract: contract,
                order: observation.work_order ? observation.work_order.task_type.name : '',
                status: (observation.closed_time ? this.$t('observations.closed') : this.$t('observations.open')),
                road_direction: observation.road_direction,
                info: observation.info ? observation.info : '',
                photos: observation.photos,
                road_address: this.getStreetAddressString(observation)
            }
        }
    }
}
</script>

<style scoped></style>
